<script lang="ts">
// @ts-ignore
import sanitizeHtml from 'sanitize-html';
import { defineComponent, computed, watch, ref, onMounted, onBeforeUnmount } from '@vue/composition-api';

import { useConfirmModule } from '@/store/global/appComponents/appconfirm';
import { CloseConfirm } from '@/store/global/appComponents/appconfirm/actions';

export default defineComponent({
    name: 'AppConfirm',
    props: {},
    // eslint-disable-next-line
    setup(props) {
        const store = useConfirmModule();

        const state = computed(() => ({
            instanceId: store.state.instanceId,
            defaultButton: store.state.defaultButton,
            distinguishFocusedButton: store.state.distinguishFocusedButton,
            acceptDisabled: store.state.acceptDisabled ? store.state.acceptDisabled() : false,
            width: store.state.width,
            message: sanitizeHtml(store.state.message, {
                allowedAttributes: {
                    span: ['class'],
                },
            }),
            title: store.state.title,

            contentClasses: store.state.contentClasses,

            attach: store.state.attach,
            acceptFn: store.state.acceptFn,
            cancelFn: store.state.cancelFn,
            isHideCancelBtn: store.state.isHideCancelBtn,

            acceptBtnColor: store.state.acceptBtnColor,
            acceptBtnClasses: store.state.acceptBtnClasses,

            acceptBtnText: store.state.acceptBtnText,
            cancelBtnText: store.state.cancelBtnText,

            extraButtons: store.state.extraButtons,

            extraHtml: store.state.extraHtmlAfterButtons,
            acceptSideButtons: store.state.acceptSideButtons,
        }));

        const isOpen = computed({
            get: () => store.state.isOpen,
            set: (val) => {
                if (val === false) {
                    store.dispatch(new CloseConfirm());
                }
            },
        });

        const onAccept = () => {
            if (!store.state.isOpen || state.value.acceptDisabled) return;
            const current = state.value;
            store.dispatch(new CloseConfirm(current.instanceId, 'selected'));
            if (current.acceptFn) current.acceptFn();
        };

        const onCancel = () => {
            if (!store.state.isOpen) return;
            const current = state.value;
            store.dispatch(new CloseConfirm(current.instanceId, 'selected'));
            if (current.cancelFn) current.cancelFn();
        };

        const onClickExtraButton = (index: number) => {
            if (!store.state.isOpen) return;
            const current = state.value;
            const button = current.extraButtons[index];
            if (!button || (button.disabled && button.disabled())) return;
            store.dispatch(new CloseConfirm(current.instanceId, 'selected'));
            if (button.callbackFn) button.callbackFn();
        };
        const onClickAcceptSideButton = (index: number) => {
            if (!store.state.isOpen) return;
            const current = state.value;
            const button = current.acceptSideButtons[index];
            if (!button || (button.disabled && button.disabled())) return;
            store.dispatch(new CloseConfirm(current.instanceId, 'selected'));
            if (button.callbackFn) button.callbackFn();
        };

        const acceptRef = ref(null);
        const cancelRef = ref(null);
        const extraRefs = ref([]);
        const focusedButton = ref<'accept' | 'cancel' | number | string>('accept');
        const focusDefault = () => {
            focusedButton.value = store.state.defaultButton;
            const button = typeof focusedButton.value === 'number' ? extraRefs.value[focusedButton.value] : acceptRef.value;
            button?.$el?.focus();
        };
        let focusTimer: ReturnType<typeof setTimeout> | undefined;
        onBeforeUnmount(() => clearTimeout(focusTimer));
        onMounted(() => {
            watch(
                () => isOpen.value ? store.state.instanceId : null,
                (current) => {
                    if (current) {
                        focusedButton.value = store.state.defaultButton;
                        clearTimeout(focusTimer);
                        const instanceId = store.state.instanceId;
                        focusTimer = setTimeout(() => {
                            if (store.state.isOpen && store.state.instanceId === instanceId) focusDefault();
                        }, 350);
                    }
                },
                { immediate: true }
            );
        });

        const left = () => {
            //@ts-ignore
            cancelRef.value?.$el?.focus();
        };
        const right = () => {
            //@ts-ignore
            acceptRef.value?.$el?.focus();
        };

        return {
            state,
            isOpen,
            onAccept,
            onCancel,

            acceptRef,
            cancelRef,
            extraRefs,
            focusedButton,

            left,
            right,

            onClickExtraButton,
            onClickAcceptSideButton,
        };
    },
});
</script>

<template>
  <v-dialog
    v-model="isOpen"
    :width="state.width"
    persistent
    :attach="state.attach"
    @click:outside.stop.prevent
    @keydown.esc="onCancel"
  >
    <v-card
      class="no-click-outside"
      :class="state.contentClasses"
      @keydown.left="left"
      @keydown.right="right"
    >
      <v-card-title class="headline">
        {{ state.title }}
      </v-card-title>

      <v-card-text v-html="state.message" />

      <v-card-actions>
        <v-btn
          v-for="(button, index) in state.extraButtons"
          :key="index"
          ref="extraRefs"
          depressed
          :disabled="button.disabled ? button.disabled() : false"
          :class="[button.class || '', { 'edit-confirmation-selected': state.distinguishFocusedButton && focusedButton === index }]"
          :color="button.color || 'primary'"
          @focus="focusedButton = index"
          @click="onClickExtraButton(index)"
        >
          {{ button.text }}
        </v-btn>

        <v-spacer />

        <!-- @TODO: Allow setting style by prop, e.g. remove(red) -->
        <v-btn
          ref="acceptRef"
          depressed
          :disabled="state.acceptDisabled"
          :class="[state.acceptBtnClasses, { 'edit-confirmation-selected': state.distinguishFocusedButton && focusedButton === 'accept' }]"
          :color="state.acceptBtnColor"
          @focus="focusedButton = 'accept'"
          @click="onAccept"
        >
          {{ state.acceptBtnText }}
        </v-btn>

        <v-btn
          v-for="(button, index) in state.acceptSideButtons"
          :key="`accept-side-${index}`"
          depressed
          :disabled="button.disabled ? button.disabled() : false"
          :class="[button.class || '', { 'edit-confirmation-selected': state.distinguishFocusedButton && focusedButton === `accept-side-${index}` }]"
          :color="button.color || 'primary'"
          :data-testid="`confirm-accept-side-${index}`"
          @focus="focusedButton = `accept-side-${index}`"
          @click="onClickAcceptSideButton(index)"
        >
          {{ button.text }}
        </v-btn>

        <v-btn
          v-if="!state.isHideCancelBtn"
          ref="cancelRef"
          color="secondaryButton"
          depressed
          class="primary--text"
          :class="{ 'edit-confirmation-selected': state.distinguishFocusedButton && focusedButton === 'cancel' }"
          @focus="focusedButton = 'cancel'"
          @click="onCancel"
        >
          {{ state.cancelBtnText }}
        </v-btn>
      </v-card-actions>
      <div v-if="state.extraHtml" v-html="state.extraHtml" />
    </v-card>
  </v-dialog>
</template>

<style scoped>
.v-btn.edit-confirmation-selected:focus::before {
    background-color: currentColor;
}
</style>
